import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useCallback } from "react";

import type { ProjectResidentsDto, ResidentsBenchmarkDto } from "@/api/types";
import { Skeleton } from "@/design-system/Skeleton";
import { Stack } from "@/design-system/Stack";
import { MAX_ANALYTICS_PERCENTAGE_VALUE } from "@/helpers/analytics";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { usePortfolioAnalyticsQueries } from "@/queries/portfolio-analytics/queryOptions";
import { useTranslation } from "@/translations";

import { TrendBadge } from "../Badge/TrendBadge";
import { OpinionatedProgressChart } from "../Charts/OpinionatedProgressChart";
import { SimpleProgressChart } from "../Charts/SimpleProgressChart";
import { HandshakeIcon } from "../Icons/HandshakeIcon";
import { MessageSquare01Icon } from "../Icons/MessageSquare01Icon";
import { UserCheck01Icon } from "../Icons/UserCheck01Icon";
import { UserPlus01Icon } from "../Icons/UserPlus01Icon";
import { AnalyticsWidgetCard } from "./AnalyticsWidgetCard";
import {
  derivePrimaryDateRangeFromPeriod,
  deriveSecondaryDateRangeFromPeriod,
  formatAnalyticsDatePayload,
  formatAnalyticsPercentageValue,
} from "./helpers";
import type { AnalyticsWidgetBaseProps } from "./types";
import { useMetricHelp } from "./useMetricHelp";

type PlatformResidentsWidgetProps = AnalyticsWidgetBaseProps & {
  type: "supportive" | "onboarded" | "engaging" | "contributing";
};

export function PlatformResidentsWidget({
  projectIds = [],
  type,
  period,
}: PlatformResidentsWidgetProps): React.ReactNode {
  const portfolioAnalyticsQueries = usePortfolioAnalyticsQueries();
  const { t } = useTranslation();
  const help = useMetricHelp();

  const secondaryDates = deriveSecondaryDateRangeFromPeriod(period);
  const { data: secondaryData } = useQuery({
    ...portfolioAnalyticsQueries.platformResidents({
      projectId: projectIds.length === 1 ? projectIds[0] : undefined,
      startDate: formatAnalyticsDatePayload(secondaryDates.startDate),
      endDate: formatAnalyticsDatePayload(secondaryDates.endDate),
    }),
    select: (res) => {
      const data = commonAPIDataSelector(res);

      return {
        ...data,
        projects: data.projects.filter((project) => projectIds.includes(project.projectId)),
      };
    },
  });

  const primaryDates = derivePrimaryDateRangeFromPeriod(period);
  const { data: primaryData, isPending } = useQuery({
    ...portfolioAnalyticsQueries.platformResidents({
      projectId: projectIds.length === 1 ? projectIds[0] : undefined,
      startDate: primaryDates.startDate ? formatAnalyticsDatePayload(primaryDates.startDate) : undefined,
      endDate: primaryDates.endDate ? formatAnalyticsDatePayload(primaryDates.endDate) : undefined,
    }),
    select: (res) => {
      const data = commonAPIDataSelector(res);

      return {
        ...data,
        projects: data.projects.filter((project) => projectIds.includes(project.projectId)),
      };
    },
  });

  const aggregateData = useCallback((projects: ProjectResidentsDto[]) => {
    return (projects ?? []).reduce(
      (acc, project) => {
        if (project.onboardedResidentCount) {
          acc.supportiveResidentCount += project.supportiveResidentCount;
          acc.onboardedResidentCount += project.onboardedResidentCount;
          acc.engagedResidentCount += project.engagedResidentCount;
          acc.contributingResidentCount += project.contributingResidentCount;
          acc.invitedResidentCount += project.invitedResidentCount;
          acc.totalResidentCount += project.invitedResidentCount + project.onboardedResidentCount;
          acc.totalBenchmark.adoptionPercentage += project.benchmark.adoptionPercentage;
          acc.totalBenchmark.engagedResidentPercentage += project.benchmark.engagedResidentPercentage;
          acc.totalBenchmark.contributingResidentPercentage += project.benchmark.contributingResidentPercentage;
          acc.amountEligibleProjects += 1;
        }

        return acc;
      },
      {
        supportiveResidentCount: 0,
        onboardedResidentCount: 0,
        engagedResidentCount: 0,
        contributingResidentCount: 0,
        invitedResidentCount: 0,
        totalResidentCount: 0,
        totalBenchmark: {
          adoptionPercentage: 0,
          engagedResidentPercentage: 0,
          contributingResidentPercentage: 0,
        } satisfies ResidentsBenchmarkDto,
        amountEligibleProjects: 0,
      },
    );
  }, []);

  const convertDecimalToPercentageValue = (decimal: number) => {
    return decimal * 100;
  };

  const primaryProjects = primaryData?.projects ?? [];
  const primaryAggregatedData = aggregateData(primaryProjects);
  const secondaryProjects = secondaryData?.projects ?? [];
  const secondaryAggregatedData = aggregateData(secondaryProjects);

  if (type === "supportive") {
    const primaryScore = convertDecimalToPercentageValue(
      primaryAggregatedData.supportiveResidentCount / primaryAggregatedData.onboardedResidentCount || 0,
    );
    const secondaryScore = convertDecimalToPercentageValue(
      secondaryAggregatedData.supportiveResidentCount / secondaryAggregatedData.onboardedResidentCount || 0,
    );

    return (
      <AnalyticsWidgetCard
        title={t("component.analytics-widget.supportive-residents.title")}
        titleTooltip={help("component.analytics-widget.supportive-residents.tooltip")}
        icon={<HandshakeIcon />}
        helperText={t("component.analytics-widget.supportive-residents.helper-text", {
          supportiveResidentCount: primaryAggregatedData.supportiveResidentCount,
          onboardedResidentCount: primaryAggregatedData.onboardedResidentCount,
        })}
        renderBadge={() => (
          <TrendBadge
            referenceValue={secondaryScore}
            value={primaryScore}
            format="percentage"
            shouldRoundUp
            isZeroOmitted
          />
        )}
        renderSkeleton={() => <WidgetSkeleton />}
        isLoading={isPending}
      >
        <SimpleProgressChart
          value={primaryScore}
          max={MAX_ANALYTICS_PERCENTAGE_VALUE}
          renderValue={formatAnalyticsPercentageValue}
        />
      </AnalyticsWidgetCard>
    );
  } else if (type === "onboarded") {
    const primaryScore = convertDecimalToPercentageValue(
      primaryAggregatedData.onboardedResidentCount / primaryAggregatedData.totalResidentCount || 0,
    );
    const secondaryScore = convertDecimalToPercentageValue(
      secondaryAggregatedData.onboardedResidentCount / secondaryAggregatedData.totalResidentCount || 0,
    );

    return (
      <AnalyticsWidgetCard
        title={t("component.analytics-widget.onboarded-residents.title")}
        titleTooltip={help("component.analytics-widget.onboarded-residents.tooltip")}
        icon={<UserPlus01Icon />}
        helperText={t("component.analytics-widget.onboarded-residents.helper-text", {
          onboardedResidentCount: primaryAggregatedData.onboardedResidentCount,
          totalResidentCount: primaryAggregatedData.totalResidentCount,
        })}
        renderBadge={() => (
          <TrendBadge
            referenceValue={secondaryScore}
            value={primaryScore}
            format="percentage"
            shouldRoundUp
            isZeroOmitted
          />
        )}
        isLoading={isPending}
        renderSkeleton={() => <WidgetSkeleton />}
      >
        <SimpleProgressChart
          value={primaryScore}
          max={MAX_ANALYTICS_PERCENTAGE_VALUE}
          renderValue={formatAnalyticsPercentageValue}
        />
      </AnalyticsWidgetCard>
    );
  } else if (type === "engaging") {
    const primaryScore = convertDecimalToPercentageValue(
      primaryAggregatedData.engagedResidentCount / primaryAggregatedData.onboardedResidentCount || 0,
    );
    const secondaryScore = convertDecimalToPercentageValue(
      secondaryAggregatedData.engagedResidentCount / secondaryAggregatedData.onboardedResidentCount || 0,
    );
    const primaryBenchmark =
      primaryAggregatedData.totalBenchmark.engagedResidentPercentage / primaryAggregatedData.amountEligibleProjects ||
      0;

    return (
      <AnalyticsWidgetCard
        title={t("component.analytics-widget.engaging-residents.title")}
        titleTooltip={help("component.analytics-widget.engaging-residents.tooltip")}
        icon={<UserCheck01Icon />}
        helperText={t("component.analytics-widget.engaging-residents.helper-text", {
          engagedResidentCount: primaryAggregatedData.engagedResidentCount,
          onboardedResidentCount: primaryAggregatedData.onboardedResidentCount,
        })}
        renderBadge={() => (
          <TrendBadge
            referenceValue={secondaryScore}
            value={primaryScore}
            format="percentage"
            shouldRoundUp
            isZeroOmitted
          />
        )}
        renderSkeleton={() => <WidgetSkeleton />}
        isLoading={isPending}
      >
        <OpinionatedProgressChart
          value={primaryScore}
          benchmark={primaryBenchmark}
          max={MAX_ANALYTICS_PERCENTAGE_VALUE}
          renderValue={formatAnalyticsPercentageValue}
        />
      </AnalyticsWidgetCard>
    );
  } else if (type === "contributing") {
    const primaryScore = convertDecimalToPercentageValue(
      primaryAggregatedData.contributingResidentCount / primaryAggregatedData.onboardedResidentCount || 0,
    );
    const secondaryScore = convertDecimalToPercentageValue(
      secondaryAggregatedData.contributingResidentCount / secondaryAggregatedData.onboardedResidentCount || 0,
    );
    const primaryBenchmark =
      primaryAggregatedData.totalBenchmark.contributingResidentPercentage /
        primaryAggregatedData.amountEligibleProjects || 0;

    return (
      <AnalyticsWidgetCard
        title={t("component.analytics-widget.socially-contributing-residents.title")}
        titleTooltip={help("component.analytics-widget.socially-contributing-residents.tooltip")}
        icon={<MessageSquare01Icon />}
        helperText={t("component.analytics-widget.socially-contributing-residents.helper-text", {
          contributingResidentCount: primaryAggregatedData.contributingResidentCount,
          onboardedResidentCount: primaryAggregatedData.onboardedResidentCount,
        })}
        renderSkeleton={() => <WidgetSkeleton />}
        renderBadge={() => (
          <TrendBadge
            referenceValue={secondaryScore}
            value={primaryScore}
            format="percentage"
            shouldRoundUp
            isZeroOmitted
          />
        )}
        isLoading={isPending}
      >
        <OpinionatedProgressChart
          value={primaryScore}
          benchmark={primaryBenchmark}
          max={MAX_ANALYTICS_PERCENTAGE_VALUE}
          renderValue={formatAnalyticsPercentageValue}
        />
      </AnalyticsWidgetCard>
    );
  }

  return null;
}

function WidgetSkeleton(): React.ReactNode {
  return (
    <Stack gap="md">
      <Stack gap="xs">
        <Skeleton height="10" width="1/4" />
        <Skeleton height="2" />
      </Stack>
      <Skeleton />
    </Stack>
  );
}
